import React from 'react';
import { SurveyDashboard as SurveyDashboardType } from '../../services/surveyService';

interface Props {
  dashboard: SurveyDashboardType;
}

const SurveyDashboard: React.FC<Props> = ({ dashboard }) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };


  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Total Surveys Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 p-3 bg-blue-100 rounded-lg">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500">Total Surveys</p>
            <p className="text-2xl font-bold text-gray-900">{dashboard.totalSurveys.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">
              {dashboard.sectionsWithData} sections
            </p>
          </div>
        </div>
      </div>

      {/* Average Rating Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 p-3 bg-yellow-100 rounded-lg">
            <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500">Average Rating</p>
            <p className="text-2xl font-bold text-gray-900">{dashboard.averageRating.toFixed(1)}</p>
            <div className="flex mt-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  className={`w-4 h-4 ${
                    star <= Math.round(dashboard.averageRating) 
                      ? 'text-yellow-400' 
                      : 'text-gray-300'
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Estimate Accuracy Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className={`flex-shrink-0 p-3 rounded-lg ${
            dashboard.estimateAccuracy >= 75 ? 'bg-green-100' : 
            dashboard.estimateAccuracy >= 50 ? 'bg-yellow-100' : 'bg-red-100'
          }`}>
            <svg className={`w-6 h-6 ${
              dashboard.estimateAccuracy >= 75 ? 'text-green-600' : 
              dashboard.estimateAccuracy >= 50 ? 'text-yellow-600' : 'text-red-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500">Estimate Accuracy</p>
            <p className="text-2xl font-bold text-gray-900">{dashboard.estimateAccuracy.toFixed(1)}%</p>
            <p className="text-xs text-gray-500 mt-1">Within ±25% of actual</p>
          </div>
        </div>
      </div>

      {/* Problem Sections Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className={`flex-shrink-0 p-3 rounded-lg ${
            dashboard.problemSections === 0 ? 'bg-green-100' : 
            dashboard.problemSections <= 5 ? 'bg-yellow-100' : 'bg-red-100'
          }`}>
            <svg className={`w-6 h-6 ${
              dashboard.problemSections === 0 ? 'text-green-600' : 
              dashboard.problemSections <= 5 ? 'text-yellow-600' : 'text-red-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500">Problem Sections</p>
            <p className="text-2xl font-bold text-gray-900">{dashboard.problemSections}</p>
            <p className="text-xs text-gray-500 mt-1">Need attention</p>
          </div>
        </div>
      </div>

      {/* Completion Stats Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 p-3 bg-emerald-100 rounded-lg">
            <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500">Completion Rate</p>
            <p className="text-2xl font-bold text-gray-900">
              {((dashboard.completedSections / (dashboard.completedSections + dashboard.skippedSections)) * 100).toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {dashboard.completedSections} completed
            </p>
          </div>
        </div>
      </div>

      {/* Skip Rate Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 p-3 bg-orange-100 rounded-lg">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500">Skip Rate</p>
            <p className="text-2xl font-bold text-gray-900">
              {((dashboard.skippedSections / (dashboard.completedSections + dashboard.skippedSections)) * 100).toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {dashboard.skippedSections} skipped
            </p>
          </div>
        </div>
      </div>

      {/* Last Survey Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 p-3 bg-purple-100 rounded-lg">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500">Last Survey</p>
            <p className="text-lg font-bold text-gray-900">
              {formatDate(dashboard.lastSurveyDate)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Most recent response</p>
          </div>
        </div>
      </div>

      {/* Difficulty Distribution Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 p-3 bg-indigo-100 rounded-lg">
            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500">Difficulty</p>
            <div className="mt-2 space-y-1">
              {Object.entries(dashboard.difficultyDistribution).map(([difficulty, count]) => (
                <div key={difficulty} className="flex items-center text-xs">
                  <span className="capitalize w-16">{difficulty}:</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyDashboard;